import { confirm } from 'components/molecules/Confirmation'
import { useNavigate, useParams } from 'react-router-dom'
import moment from 'moment'
import Toast from '../../atoms/Toast'
import { useLabPermission } from 'hooks/useLabPermission'
import DashboardContent from '../atoms/DashboardContent'
import { UserPermission } from '../user/permissions'
import {
  useCreateLabDeviceMutation,
  useDeleteLabDeviceMutation,
  useUpdateLabDeviceMutation,
} from 'store/services/devices'
import routes from '../constants/routes'
import CreateDeviceForm from './CreateDeviceForm'
import { DeviceAccessories } from './DeviceAccessories'
import { DeviceShelfLife } from './DeviceShelfLife'
import PatientsList from "./PatientsList";
import { DeviceComplaintsList } from 'components/organisms/deviceComplaints/DeviceComplaintsList'

export interface DeviceFormValues {
  brandId: number | null
  label: string
  labelOverride: boolean
  serial: string
  mac: string
  bluetoothId: string
  state: string
  statusUpdatedAt: Date
  officeId: string
  enabled: boolean
  purchaseCost: number | null
  salePrice: number | null
  notes: string
  usedForWesperDropship?: boolean
}

const DeviceUpsert = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const canManageDevices = useLabPermission(UserPermission.MANAGE_DEVICES)
  const canManageDeviceInventory = useLabPermission(UserPermission.MANAGE_DEVICE_INVENTORY)
  const [createDevice] = useCreateLabDeviceMutation()
  const [updateDevice] = useUpdateLabDeviceMutation()
  const [deleteLabDevice] = useDeleteLabDeviceMutation()

  // TES-2881: without MANAGE_DEVICES, show the device read-only instead of bouncing.
  const readOnly = !canManageDevices

  return (
    <div>
      <DashboardContent
        title={id ? (readOnly ? 'View Device' : 'Edit Device') : 'Create Device'}
        icon="fa fa-hdd"
        content={
          <div className="row flex-lg-nowrap mw-100" style={{columnGap: '8px'}}>
            <div className="col-lg-5 card card-primary p-4">
            <CreateDeviceForm
              id={id ? Number(id) : undefined}
              readOnly={readOnly}
              canManageDeviceInventory={canManageDeviceInventory}
              onSubmit={async (values) => {
                const submitValues = {
                  ...values,
                  ...(!values.officeId && { officeId: null }),
                  statusUpdatedAt: values.statusUpdatedAt
                    ? moment(values.statusUpdatedAt).format('YYYY-MM-DD')
                    : undefined,
                }

                try {
                  if (id) {
                    await updateDevice({ id: Number(id), ...submitValues }).unwrap()
                    Toast({ type: 'success', label: `Device updated!` })
                  } else {
                    await createDevice(submitValues).unwrap()
                    Toast({ type: 'success', label: `Device created!` })
                  }

                  navigate(`${routes.index}${routes.device.root}`)
                } catch (e) {
                  Toast({ type: 'error', label: e.data.message })
                }
              }}
              onDelete={
                !!id
                  ? () => {
                      confirm('', {
                        title: 'Are you sure?',
                        description: 'This will delete selected device.',
                        yesAction: 'Yes, proceed',
                        noAction: 'Cancel',
                      })
                        .then(async () => {
                          try {
                            await deleteLabDevice(Number(id)).unwrap()

                            Toast({ type: 'success', label: `Device deleted!` })
                            navigate(`${routes.index}${routes.device.root}`)
                          } catch (e) {
                            Toast({ type: 'error', label: e.data.message })
                          }
                        })
                        .catch(() => {})
                    }
                  : undefined
              }
            />
          </div>
              <div className="col-lg-7">
                <div className="card card-primary p-4">
                  {id && (
                    <div className="row">
                      <DeviceShelfLife deviceId={Number(id)} />
                      <DeviceAccessories deviceId={Number(id)} />
                    </div>
                  )}
                  <PatientsList />
                </div>
                {id && (
                  <div className="card card-primary p-4">
                    <DeviceComplaintsList site="lab" deviceId={Number(id)} canManage={canManageDevices} />
                  </div>
                )}
              </div>
          </div>
        }
        breadcrumb={[
          {
            label: 'Devices',
            to: `${routes.index}${routes.device.root}`,
          },
          { label: id ? 'Edit' : 'New' },
        ]}
      />
    </div>
  )
}

export default DeviceUpsert
